import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  Building, 
  Plus, 
  Trash2, 
  AlertCircle, 
  Briefcase,
  Loader2,
  Copy,
  Download
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { createPageUrl } from "@/utils";
import { useNavigate } from "react-router-dom";

// Função para formatar CNPJ
function formatCnpj(value) {
  if (!value) return "";
  const numbersOnly = value.replace(/\D/g, "");
  const cnpjNumbers = numbersOnly.slice(0, 14);
  if (cnpjNumbers.length <= 2) {
    return cnpjNumbers;
  } else if (cnpjNumbers.length <= 5) {
    return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2)}`;
  } else if (cnpjNumbers.length <= 8) {
    return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2, 5)}.${cnpjNumbers.slice(5)}`;
  } else if (cnpjNumbers.length <= 12) {
    return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2, 5)}.${cnpjNumbers.slice(5, 8)}/${cnpjNumbers.slice(8)}`;
  } else {
    return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2, 5)}.${cnpjNumbers.slice(5, 8)}/${cnpjNumbers.slice(8, 12)}-${cnpjNumbers.slice(12, 14)}`;
  }
}

export default function BensEmpresa() {
  const [cnpjs, setCnpjs] = useState([""]);
  const [inputTouched, setInputTouched] = useState([false]);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [processedCnpjs, setProcessedCnpjs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Adiciona novo campo de CNPJ
  const handleAddCnpj = () => {
    setCnpjs([...cnpjs, ""]);
    setInputTouched([...inputTouched, false]);
  };

  // Remove campo de CNPJ
  const handleRemoveCnpj = (idx) => {
    setCnpjs(cnpjs.filter((_, i) => i !== idx));
    setInputTouched(inputTouched.filter((_, i) => i !== idx));
  };

  // Atualiza valor do CNPJ individualmente
  const handleCnpjChange = (idx, value) => {
    const updated = [...cnpjs];
    updated[idx] = formatCnpj(value);
    setCnpjs(updated);
    const touchedArr = [...inputTouched];
    touchedArr[idx] = true;
    setInputTouched(touchedArr);
  };

  // Validação simples de CNPJ
  const isValidCnpj = (value) => value.replace(/\D/g, "").length === 14;

  // Função para buscar informações do CNPJ
  const fetchCnpjInfo = async (cnpjValue) => {
    try {
      // Limpa o CNPJ, deixando apenas números
      const cleanCnpj = cnpjValue.replace(/\D/g, '');
      
      const response = await fetch('https://gateway.apibrasil.io/api/v2/dados/cnpj/credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL2dhdGV3YXkuYXBpYnJhc2lsLmlvL2FwaS92Mi9hdXRoL3JlZ2lzdGVyIiwiaWF0IjoxNzM4MjM5OTc4LCJleHAiOjE3Njk3NzU5NzgsIm5iZiI6MTczODIzOTk3OCwianRpIjoiempUeDVqNXRJdmw5Q0twNSIsInN1YiI6IjEzNDc1IiwicHJ2IjoiMjNiZDVjODk0OWY2MDBhZGIzOWU3MDFjNDAwODcyZGI3YTU5NzZmNyJ9.ZX3iQRr0BK3j8r-qkHz1JyKJ0eD2o68I8PV3E2h_paA`
        },
        body: JSON.stringify({
          tipo: 'cnpj',
          cnpj: cleanCnpj
        })
      });
      
      if (!response.ok) {
        throw new Error(`Erro na consulta do CNPJ: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.message || 'Erro ao consultar CNPJ');
      }
      
      return data.response.cnpj;
    } catch (err) {
      console.error('Erro ao buscar informações do CNPJ:', err);
      throw err;
    }
  };

  // Função para processar o CNPJ e gerar a estrutura adequada para o IRPF
  const processCnpjForIRPF = (cnpjData) => {
    // Verificar data de abertura da empresa
    const dataAbertura = cnpjData.data_inicio_atividades;
    const anoAbertura = parseInt(dataAbertura.substring(0, 4));
    
    // Ano atual é 2025, declaração é de 2024, então verificamos se empresa existia em 2023/2024
    const anoAtual = 2025;
    const anoDeclaracao = anoAtual - 1; // 2024
    const anoAnterior = anoDeclaracao - 1; // 2023
    
    // Se a empresa foi aberta em 2025, não deve ser incluída
    if (anoAbertura >= anoAtual) {
      return null;
    }
    
    // Verificar quantidade de sócios para determinar a descrição
    const quantidadeSocios = cnpjData.socios ? cnpjData.socios.length : 0;
    const razaoSocial = cnpjData.empresa.razao_social;
    const cnpjFormatado = cnpjData.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    
    // Construir a descrição conforme o formato especificado
    let descricao = "POSSUI ";
    
    // Adicionar "100% de" quando for um único sócio ou quando o array de sócios estiver vazio
    if (quantidadeSocios === 1 || (Array.isArray(cnpjData.socios) && cnpjData.socios.length === 0)) {
      descricao += "100% DE ";
    }
    
    // Completar a descrição
    descricao += `PARTICIPAÇÃO NO CAPITAL SOCIAL DA EMPRESA ${razaoSocial}, INSCRITO NO CNPJ Nº ${cnpjFormatado}`;
    
    // Determinar os valores do capital social
    const capitalSocial = cnpjData.empresa.capital_social;
    let situacao2023 = null;
    let situacao2024 = capitalSocial;
    
    // Se a empresa foi aberta antes ou em 2023, preencher o campo de 2023
    if (anoAbertura <= anoAnterior) {
      situacao2023 = capitalSocial;
    }
    
    return {
      grupo: "03",
      codigo: "02",
      cnpj: cnpjData.cnpj,
      descricao: descricao,
      situacao_2023: situacao2023,
      situacao_2024: situacao2024
    };
  };

  // Função para processar todos os CNPJs
  const processAllCnpjs = async () => {
    try {
      // Verificar se há CNPJs válidos para processar
      const validCnpjs = cnpjs.filter(cnpj => isValidCnpj(cnpj));
      if (validCnpjs.length === 0) {
        setError("Nenhum CNPJ válido para processar.");
        return;
      }
      
      setProcessing(true);
      setError(null);
      setProcessedCnpjs([]);
      
      const processedData = {
        IRPFBens: [
          {
            Tasks: {
              Empresa: []
            }
          }
        ]
      };
      
      // Processar cada CNPJ individualmente
      for (const cnpj of validCnpjs) {
        try {
          // Buscar informações do CNPJ
          const cnpjData = await fetchCnpjInfo(cnpj);
          
          // Processar os dados para IRPF
          const processedCnpj = processCnpjForIRPF(cnpjData);
          
          // Se o processamento retornou um resultado válido, adicionar ao array
          if (processedCnpj) {
            processedData.IRPFBens[0].Tasks.Empresa.push(processedCnpj);
            setProcessedCnpjs(prev => [...prev, {
              cnpj: cnpj,
              razaoSocial: cnpjData.empresa.razao_social,
              status: 'sucesso'
            }]);
          } else {
            setProcessedCnpjs(prev => [...prev, {
              cnpj: cnpj,
              status: 'ignorado',
              motivo: 'Empresa aberta após o período de declaração'
            }]);
          }
        } catch (err) {
          console.error(`Erro ao processar CNPJ ${cnpj}:`, err);
          setProcessedCnpjs(prev => [...prev, {
            cnpj: cnpj,
            status: 'erro',
            motivo: err.message
          }]);
        }
      }
      
      // Definir o resultado processado
      setResult(processedData);
    } catch (err) {
      setError(`Erro ao processar CNPJs: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Simulação de envio de formulário
  const handleSubmit = () => {
    // Validar CNPJs
    const invalidCnpjs = cnpjs.filter(cnpj => cnpj.trim() !== "" && !isValidCnpj(cnpj));
    if (invalidCnpjs.length > 0) {
      setError("Por favor, insira CNPJ(s) válido(s).");
      return;
    }
    
    // Iniciar processamento dos CNPJs
    processAllCnpjs();
  };

  // Função para copiar o JSON de resultado
  const copyResultToClipboard = () => {
    if (!result) return;
    
    const resultString = JSON.stringify(result, null, 2);
    navigator.clipboard.writeText(resultString);
    alert("JSON copiado para a área de transferência!");
  };

  // Função para baixar o JSON como arquivo
  const downloadJsonFile = () => {
    if (!result) return;
    
    const resultString = JSON.stringify(result, null, 2);
    const blob = new Blob([resultString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Empresas.json';
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="container mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Link to={createPageUrl("Operations")}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <Briefcase className="h-8 w-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-gray-900">Bens e Direitos {'>'} Empresa</h1>
          </div>
        </div>
        <p className="text-gray-500">
          Adicione os CNPJs das empresas para declaração de bens e direitos no IRPF. Cada CNPJ será processado individualmente.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciamento de Empresas</CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm mb-6">
                  <p className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </p>
                </div>
              )}

              <div className="space-y-6">
                {cnpjs.map((cnpj, idx) => (
                  <div key={idx} className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label htmlFor={`cnpj-${idx}`} className="block mb-2">
                        CNPJ da Empresa {cnpjs.length > 1 ? `#${idx + 1}` : ""}
                      </Label>
                      <div className="relative">
                        <Building className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                        <Input
                          id={`cnpj-${idx}`}
                          type="text"
                          placeholder="00.000.000/0000-00"
                          value={cnpj}
                          maxLength={18}
                          onChange={e => handleCnpjChange(idx, e.target.value)}
                          className={`pl-10 font-mono ${inputTouched[idx] && !isValidCnpj(cnpj) ? "border-red-400 focus:border-red-500" : ""}`}
                          disabled={processing}
                        />
                      </div>
                      {inputTouched[idx] && !isValidCnpj(cnpj) && (
                        <p className="text-xs text-red-500 mt-1">CNPJ inválido</p>
                      )}
                    </div>
                    {cnpjs.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:bg-red-50 mb-1"
                        onClick={() => handleRemoveCnpj(idx)}
                        disabled={processing}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                ))}

                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex items-center gap-2 text-sky-700 border-sky-300 hover:bg-sky-50"
                    onClick={handleAddCnpj}
                    disabled={processing}
                  >
                    <Plus className="w-4 h-4" /> Adicionar outro CNPJ
                  </Button>
                  
                  <Button 
                    type="button"
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                    onClick={handleSubmit}
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      'Processar CNPJs'
                    )}
                  </Button>
                </div>

                {processedCnpjs.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-semibold text-gray-800 mb-4">Resultado do Processamento:</h3>
                    <div className="space-y-2">
                      {processedCnpjs.map((item, index) => (
                        <div 
                          key={index} 
                          className={`p-3 rounded-md ${
                            item.status === 'sucesso' 
                              ? 'bg-green-50 border border-green-200' 
                              : item.status === 'ignorado'
                                ? 'bg-yellow-50 border border-yellow-200'
                                : 'bg-red-50 border border-red-200'
                          }`}
                        >
                          <p className="font-mono text-sm">
                            <span className="font-medium">{item.cnpj}:</span> {' '}
                            {item.status === 'sucesso' 
                              ? <span className="text-green-700">Processado com sucesso - {item.razaoSocial}</span>
                              : <span className="text-red-700">{item.motivo}</span>
                            }
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result && (
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-semibold text-gray-800">JSON de Saída:</h3>
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          variant="ghost" 
                          className="flex items-center gap-1 text-blue-600" 
                          onClick={copyResultToClipboard}
                        >
                          <Copy className="h-4 w-4" /> Copiar
                        </Button>
                        <Button 
                          size="sm"
                          variant="ghost" 
                          className="flex items-center gap-1 text-green-600" 
                          onClick={downloadJsonFile}
                        >
                          <Download className="h-4 w-4" /> Baixar
                        </Button>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
                      <pre className="text-sm text-gray-900">
                        {JSON.stringify(result, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">O que é isso?</h3>
                <p className="text-sm text-gray-600">
                  Esta ferramenta permite cadastrar CNPJs de empresas para declaração de bens e direitos no Imposto de Renda Pessoa Física (IRPF).
                </p>
                
                <h3 className="font-semibold text-gray-800">Como usar</h3>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal ml-4">
                  <li>Insira o CNPJ completo da empresa</li>
                  <li>Adicione mais CNPJs caso necessário</li>
                  <li>Verifique se todos os CNPJs estão válidos</li>
                  <li>Clique em "Processar CNPJs"</li>
                </ol>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm mt-4">
                  <p className="flex items-start gap-2">
                    <span className="mt-0.5 flex-shrink-0">Dica:</span>
                    <span>Você pode adicionar quantos CNPJs precisar, cada um será processado individualmente.</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
